import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'
import type { Segment } from '@/types/tour'
import {
  buildTrackProfileAnalyses,
  findNearestTrackProfilePoint,
  TRACK_PROFILE_METRICS,
  type TrackProfileAnalysis,
  type TrackProfileLeg,
  type TrackProfileMetricId,
  type TrackProfilePoint,
} from '@/utils/trackProfileAnalysis'

type TrackHoverPopupOptions = {
  title: string
  currentDistanceM?: number | null
  elevationM?: number | null
  sectionDistanceM?: number | null
  segmentDistanceM?: number | null
  metricId?: TrackProfileMetricId
  categoryLabel?: string | null
  rawValue?: string | null
  metrics?: TrackHoverPopupMetric[]
}

export type TrackHoverPopupMetric = {
  metricId: TrackProfileMetricId
  label: string
  categoryLabel: string
  rawValue: string | null
  color: string
}

export type DetailedTrackHoverInfo = {
  currentDistanceM: number
  elevationM: number | null
  sectionDistanceM: number | null
  metrics: TrackHoverPopupMetric[]
}

type TrackHoverCoordinate = [number, number]
type TrackHoverCursor = {
  lat: number
  lng: number
}

const DETAILED_METRIC_ORDER: TrackProfileMetricId[] = ['gradient', 'wayType', 'surface']
const FALLBACK_SWATCH_COLOR = '#94a3b8'

export const escapeHtml = (input: string) =>
  input.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;'
      case '<':
        return '&lt;'
      case '>':
        return '&gt;'
      case '"':
        return '&quot;'
      case "'":
        return '&#39;'
      default:
        return character
    }
  })

const formatDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`

const formatElevation = (elevationM: number) => `${Math.round(elevationM)} m`

const sanitizeSwatchColor = (color: string) =>
  /^#[0-9a-fA-F]{3,8}$/.test(color.trim()) ? color.trim() : FALLBACK_SWATCH_COLOR

const resolveMetricLabel = (metricId?: TrackProfileMetricId) =>
  TRACK_PROFILE_METRICS.find((metric) => metric.id === metricId)?.label ?? 'Segment'

const buildMetricLineHtml = (metric: TrackHoverPopupMetric) => {
  const rawValueHtml = metric.rawValue
    ? ` <span class="text-slate-400">·</span> <span>${escapeHtml(metric.rawValue)}</span>`
    : ''
  return (
    '<div class="flex items-center gap-1.5 text-xs text-slate-600">' +
    `<span style="width:8px;height:8px;border-radius:9999px;background:${escapeHtml(sanitizeSwatchColor(metric.color))};display:inline-block;flex-shrink:0;"></span>` +
    `<span>${escapeHtml(metric.label)} · ${escapeHtml(metric.categoryLabel)}${rawValueHtml}</span>` +
    '</div>'
  )
}

export function buildTrackHoverPopupHtml(options: TrackHoverPopupOptions): string {
  const lines = [`<div class="font-semibold text-sm">${escapeHtml(options.title)}</div>`]

  if (typeof options.currentDistanceM === 'number' && Number.isFinite(options.currentDistanceM)) {
    lines.push(
      `<div class="text-xs text-slate-600">Route km · ${escapeHtml(formatDistance(options.currentDistanceM))}</div>`,
    )
  }

  if (typeof options.elevationM === 'number' && Number.isFinite(options.elevationM)) {
    lines.push(
      `<div class="text-xs text-slate-600">HM · ${escapeHtml(formatElevation(options.elevationM))}</div>`,
    )
  }

  if (typeof options.sectionDistanceM === 'number' && Number.isFinite(options.sectionDistanceM)) {
    lines.push(
      `<div class="text-xs text-slate-600">Section length · ${escapeHtml(formatDistance(options.sectionDistanceM))}</div>`,
    )
  }

  if (typeof options.segmentDistanceM === 'number' && Number.isFinite(options.segmentDistanceM)) {
    lines.push(
      `<div class="text-xs text-slate-600">Section length · ${escapeHtml(formatDistance(options.segmentDistanceM))}</div>`,
    )
  }

  if (options.metricId && options.categoryLabel) {
    const detailParts = [resolveMetricLabel(options.metricId), options.categoryLabel]
    if (options.rawValue) {
      detailParts.push(options.rawValue)
    }
    lines.push(`<div class="text-xs text-slate-600">${escapeHtml(detailParts.join(' · '))}</div>`)
  }

  if (options.metrics?.length) {
    lines.push(...options.metrics.map((metric) => buildMetricLineHtml(metric)))
  }

  return `<div class="space-y-0.5">${lines.join('')}</div>`
}

function estimateHoveredProfileDistanceM(
  points: TrackProfilePoint[],
  cursor: TrackHoverCursor,
): number | null {
  if (points.length === 0) return null

  let nearestPoint = points[0]
  let nearestDistance = Number.POSITIVE_INFINITY

  for (const point of points) {
    const pointDistance = calculateHaversineDistance(cursor.lat, cursor.lng, point.lat, point.lon)
    if (pointDistance < nearestDistance) {
      nearestDistance = pointDistance
      nearestPoint = point
    }
  }

  return nearestPoint.distanceM
}

function resolveAnalysisLegAtDistance(
  analysis: TrackProfileAnalysis,
  distanceM: number,
): TrackProfileLeg | null {
  if (analysis.legs.length === 0) return null

  return (
    analysis.legs.find((leg) => distanceM >= leg.startDistanceM && distanceM <= leg.endDistanceM) ??
    analysis.legs[analysis.legs.length - 1]
  )
}

const getWaySurfaceSectionKey = (
  analyses: Record<'wayType' | 'surface', TrackProfileAnalysis>,
  legIndex: number,
) => {
  const wayTypeLeg = analyses.wayType.legs[legIndex]
  const surfaceLeg = analyses.surface.legs[legIndex]
  if (!wayTypeLeg || !surfaceLeg) return null

  return [
    wayTypeLeg.categoryId,
    wayTypeLeg.rawValue ?? '',
    surfaceLeg.categoryId,
    surfaceLeg.rawValue ?? '',
  ].join('|')
}

function resolveWaySurfaceSectionDistanceM(
  analyses: Record<'wayType' | 'surface', TrackProfileAnalysis>,
  distanceM: number,
): number | null {
  const legs = analyses.wayType.legs
  if (legs.length === 0 || legs.length !== analyses.surface.legs.length) return null

  const legIndex = legs.findIndex(
    (leg) => distanceM >= leg.startDistanceM && distanceM <= leg.endDistanceM,
  )
  if (legIndex < 0) return null

  const sectionKey = getWaySurfaceSectionKey(analyses, legIndex)
  if (!sectionKey) return null

  let startIndex = legIndex
  while (startIndex > 0 && getWaySurfaceSectionKey(analyses, startIndex - 1) === sectionKey) {
    startIndex -= 1
  }

  let endIndex = legIndex
  while (
    endIndex < legs.length - 1 &&
    getWaySurfaceSectionKey(analyses, endIndex + 1) === sectionKey
  ) {
    endIndex += 1
  }

  const sectionDistanceM = legs[endIndex].endDistanceM - legs[startIndex].startDistanceM
  return Number.isFinite(sectionDistanceM) ? sectionDistanceM : null
}

export function buildDetailedTrackHoverInfo(
  segments: Segment[],
  cursor: TrackHoverCursor,
  fallbackColor = '#3b82f6',
): DetailedTrackHoverInfo | null {
  const analyses = buildTrackProfileAnalyses(segments, fallbackColor)
  const profilePoints = analyses.gradient.points
  if (profilePoints.length < 2) return null

  const currentDistanceM = estimateHoveredProfileDistanceM(profilePoints, cursor)
  if (currentDistanceM === null || !Number.isFinite(currentDistanceM)) return null

  const nearestPoint = findNearestTrackProfilePoint(profilePoints, currentDistanceM)
  const metrics = DETAILED_METRIC_ORDER.flatMap((metricId) => {
    const analysis = analyses[metricId]
    const leg = resolveAnalysisLegAtDistance(analysis, currentDistanceM)
    if (!leg) return []

    return [
      {
        metricId,
        label: resolveMetricLabel(metricId),
        categoryLabel: leg.categoryLabel,
        rawValue: leg.rawValue,
        color: leg.color,
      },
    ]
  })

  return {
    currentDistanceM,
    elevationM: nearestPoint?.ele ?? null,
    sectionDistanceM: resolveWaySurfaceSectionDistanceM(analyses, currentDistanceM),
    metrics,
  }
}

export function estimateHoveredDistanceM(
  coordinates: TrackHoverCoordinate[],
  cursor: TrackHoverCursor,
): number | null {
  if (coordinates.length === 0) return null
  if (coordinates.length === 1) return 0

  let nearestIndex = 0
  let nearestDistance = Number.POSITIVE_INFINITY
  let cumulativeDistance = 0
  const cumulativeDistances = [0]

  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1]
    const current = coordinates[index]
    cumulativeDistance += calculateHaversineDistance(
      previous[1],
      previous[0],
      current[1],
      current[0],
    )
    cumulativeDistances.push(cumulativeDistance)
  }

  for (let index = 0; index < coordinates.length; index += 1) {
    const [lon, lat] = coordinates[index]
    const pointDistance = calculateHaversineDistance(cursor.lat, cursor.lng, lat, lon)
    if (pointDistance < nearestDistance) {
      nearestDistance = pointDistance
      nearestIndex = index
    }
  }

  return cumulativeDistances[nearestIndex] ?? null
}
